// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Asset rollforward report route component.
 *
 * Displays asset flow analysis showing how assets move from starting
 * position through various changes to ending position over a date range.
 * Supports multiple asset types: inventory, fixed-assets, sold-assets.
 */
import { handleAPIError } from '@/api/error-utils';
import { useActiveAssetCostCurrency } from '@/asset/hooks/use-active-asset-cost-currency';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { ConditionalTooltip } from '@/components/conditional-tooltip';
import { PageContent } from '@/components/page-content';
import { SupportSnackbar } from '@/components/support-snackbar';
import { CurrencySelector } from '@/costs/components/currency-selector';
import {
  IsoCurrencyCode,
  isoCurrencyCodeSchema,
} from '@/currency/constants/iso';
import { useDownloadFile } from '@/hooks/use-download-file';
import { AssetRollforwardSummaryCards } from '@/report/components/asset-rollforward-summary-cards';
import { AssetWaterfallChart } from '@/report/components/asset-waterfall-chart';
import { AssetWaterfallControls } from '@/report/components/asset-waterfall-controls';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportDateRangeInput } from '@/report/components/report-date-range-input';
import { ReportExportButton } from '@/report/components/report-export-button';
import { RollforwardReportLoader } from '@/report/components/rollforward-report-loader';
import { useFixedAssetsRollforwardData } from '@/report/hooks/use-fixed-assets-rollforward-data';
import { useInventoryRollforwardData } from '@/report/hooks/use-inventory-rollforward-data';
import {
  RollforwardWaterfallDisplayMetricType,
  assetRollforwardTypes,
} from '@/report/types';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Asset rollforward report';

/** Backend export endpoint for each rollforward tab */
const EXPORT_ENDPOINTS: Record<string, string> = {
  inventory: '/v1/report/asset/inventory-rollforward/export',
  'fixed-assets': '/v1/report/asset/fixed-rollforward/export',
};

const EXPORT_BUTTON_LABELS: Record<string, string> = {
  inventory: 'Export inventory rollforward for all currencies',
  'fixed-assets': 'Export fixed asset rollforward for all currencies',
};

const searchSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
  // The selected cost currency is shared across tabs so it persists on tab clicks. A code
  // outside the app-allowed set reaches the backend and is rejected there with a 400.
  currency: isoCurrencyCodeSchema.optional().catch(undefined),
});

export const Route = createFileRoute(
  '/_app/reports/asset-rollforward/$tabType'
)({
  component: AssetRollforwardReportRoute,
  validateSearch: searchSchema,
});

function AssetRollforwardReportRoute() {
  const search = Route.useSearch() as {
    start?: string;
    end?: string;
    currency?: IsoCurrencyCode;
  };
  const params = Route.useParams();
  const navigate = Route.useNavigate();
  const { user } = useCurrentUser();

  // One cost currency shared by both tabs (URL-persisted), clamped to one the org actually holds.
  const { availableCurrencies, activeCurrencyCode, isResolved } =
    useActiveAssetCostCurrency(search.currency);

  // Keep the URL on the currency actually rendered, so a clamped deep link is shareable.
  useEffect(() => {
    if (isResolved && search.currency !== activeCurrencyCode) {
      navigate({
        search: (previous) => ({ ...previous, currency: activeCurrencyCode }),
        replace: true,
      });
    }
  }, [activeCurrencyCode, isResolved, navigate, search.currency]);

  const [metricType, setMetricType] =
    useState<RollforwardWaterfallDisplayMetricType>('units');
  const [isLoaderComplete, setIsLoaderComplete] = useState(false);

  // Calculate default date range if not provided in search params
  const endDate = search.end
    ? DateTime.fromISO(search.end)
    : DateTime.now().endOf('month');
  const startDate = search.start
    ? DateTime.fromISO(search.start)
    : DateTime.now().startOf('month');

  // Validate that start date is before or equal to end date
  const isInvalidDateRange = startDate > endDate;

  // Only fetch data for the active tab. Waiting on the resolved currency keeps a provisional
  // one from spending a full fetch the clamp would discard.
  const inventoryRollforwardData = useInventoryRollforwardData({
    startDate: isInvalidDateRange ? undefined : startDate.toISODate(),
    endDate: isInvalidDateRange ? undefined : endDate.toISODate(),
    enabled: isResolved && params.tabType === 'inventory',
    activeCurrencyCode,
  });

  const fixedAssetsRollforwardData = useFixedAssetsRollforwardData({
    startDate: isInvalidDateRange ? undefined : startDate.toISODate(),
    endDate: isInvalidDateRange ? undefined : endDate.toISODate(),
    enabled: isResolved && params.tabType === 'fixed-assets',
    activeCurrencyCode,
  });

  // Each response already covers just the active currency's waterfall.
  const activeInventoryBucket = inventoryRollforwardData.data;
  const activeFixedBucket = fixedAssetsRollforwardData.data;

  // A waterfall has data when any phase carries assets.
  const inventoryHasData = activeInventoryBucket
    ? [
        activeInventoryBucket.start,
        activeInventoryBucket.added,
        activeInventoryBucket.deployed,
        activeInventoryBucket.sold,
        activeInventoryBucket.scrapped,
        activeInventoryBucket.end,
      ].some((phase) => phase.assets.length > 0)
    : false;
  const fixedHasData = activeFixedBucket
    ? [
        activeFixedBucket.start,
        activeFixedBucket.added,
        activeFixedBucket.depreciated,
        activeFixedBucket.sold,
        activeFixedBucket.scrapped,
        activeFixedBucket.end,
      ].some((phase) => phase.assets.length > 0)
    : false;

  const isDateRangeError =
    (inventoryRollforwardData.isError &&
      inventoryRollforwardData.error !== null &&
      handleAPIError(inventoryRollforwardData.error).errorType ===
        'date_range_exceeded') ||
    (fixedAssetsRollforwardData.isError &&
      fixedAssetsRollforwardData.error !== null &&
      handleAPIError(fixedAssetsRollforwardData.error).errorType ===
        'date_range_exceeded');

  // Determine loading state based on current tab
  const isLoading =
    params.tabType === 'inventory'
      ? inventoryRollforwardData.isLoading
      : fixedAssetsRollforwardData.isLoading;

  // Reset on the fetch itself rather than on its inputs, so any refetch shows the loader --
  // a currency switch changes the query key too, and keying on inputs missed it.
  useEffect(() => {
    if (isLoading) {
      setIsLoaderComplete(false);
    }
  }, [isLoading]);

  // Show loader when loading and loader animation not complete
  const showLoader = isLoading && !isLoaderComplete && !isInvalidDateRange;

  const handleLoaderComplete = () => {
    setIsLoaderComplete(true);
  };

  const handleChangeDateRange = (dateRange: DateRange<DateTime>) => {
    const start = dateRange[0]?.toISODate();
    const end = dateRange[1]?.toISODate();

    navigate({ search: { ...search, start, end } });
  };

  const handleCurrencyChange = (currencyCode: IsoCurrencyCode) => {
    navigate({ search: { ...search, currency: currencyCode } });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newTabType = assetRollforwardTypes[newValue];

    if (newTabType) {
      navigate({
        search: { ...search },
        params: { tabType: newTabType },
      });
    }
  };

  const downloadExport = useDownloadFile();

  /**
   * Downloads the current tab's backend-generated Excel export (one sheet per
   * currency) via an authenticated request, saved under the server's filename.
   */
  const handleExport = () => {
    const endpoint = EXPORT_ENDPOINTS[params.tabType];
    if (!endpoint) {
      return;
    }
    const query = new URLSearchParams();
    query.set('startDate', startDate.toISODate() ?? '');
    query.set('endDate', endDate.toISODate() ?? '');
    downloadExport.mutate({
      path: endpoint,
      params: query,
      fallbackFilename: `${params.tabType}-rollforward.xlsx`,
    });
  };

  const canExport = (): boolean => {
    if (isInvalidDateRange) {
      return false;
    }

    switch (params.tabType) {
      case 'inventory':
        return (
          !inventoryRollforwardData.isLoading &&
          inventoryRollforwardData.data !== undefined
        );
      case 'fixed-assets':
        return (
          !fixedAssetsRollforwardData.isLoading &&
          fixedAssetsRollforwardData.data !== undefined
        );
      default:
        return false;
    }
  };

  // Get current tab index based on the URL parameter
  const currentTabIndex = params.tabType === 'inventory' ? 0 : 1;

  const interval = isInvalidDateRange
    ? DateTime.now().startOf('month').until(DateTime.now().endOf('month'))
    : startDate.until(endDate);

  return (
    <PageContent
      sx={{
        gap: 1,
        flex: 1,
        display: 'grid',
        maxHeight: '100%',
        gridTemplateRows: 'repeat(4, auto) 1fr',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack>
          <ReportBreadcrumbs title={REPORT_TITLE} parent="accounting" />
          <Typography variant="h5">{REPORT_TITLE}</Typography>
        </Stack>
        <ConditionalTooltip
          arrow
          placement="left"
          tooltipText={canExport() ? undefined : 'No data available to export'}
        >
          <ReportExportButton
            label={
              downloadExport.isPending
                ? 'Exporting...'
                : (EXPORT_BUTTON_LABELS[params.tabType] ??
                  'Export rollforward details')
            }
            isPending={downloadExport.isPending}
            onClick={handleExport}
            disabled={!canExport()}
          />
        </ConditionalTooltip>
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <AssetWaterfallControls
          metricType={metricType}
          onMetricTypeChange={setMetricType}
          currencyCode={activeCurrencyCode}
        />
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          {availableCurrencies.length > 0 && (
            <CurrencySelector
              currencies={availableCurrencies}
              value={activeCurrencyCode}
              onChange={handleCurrencyChange}
            />
          )}
          <ReportDateRangeInput
            interval={interval}
            timezone={user.organization.timezone}
            onChange={handleChangeDateRange}
            error={isInvalidDateRange}
            helperText={
              isInvalidDateRange
                ? 'Start date cannot be after end date'
                : undefined
            }
            hideReportedFor
          />
        </Stack>
      </Stack>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTabIndex} onChange={handleTabChange}>
          <Tab label="Inventory assets" />
          <Tab label="Fixed assets" />
        </Tabs>
      </Box>

      {params.tabType === 'inventory' && (
        <>
          {isDateRangeError && (
            <Alert severity="error">
              The date range selected exceeds the maximum allowed range. Please
              select a smaller date range.
            </Alert>
          )}

          {showLoader ? (
            <RollforwardReportLoader
              reportType="inventory"
              isDataReady={!inventoryRollforwardData.isLoading}
              onComplete={handleLoaderComplete}
            />
          ) : inventoryRollforwardData.isError && !isDateRangeError ? (
            <Alert severity="error">
              Unable to load inventory rollforward data. Please try again.
            </Alert>
          ) : !inventoryHasData &&
            !isDateRangeError &&
            !isInvalidDateRange &&
            !inventoryRollforwardData.isError ? (
            <Alert severity="info">
              No inventory rollforward data for the selected date range
            </Alert>
          ) : (
            <Stack spacing={3}>
              <AssetRollforwardSummaryCards
                data={activeInventoryBucket}
                currencyCode={activeCurrencyCode}
                metricType={metricType}
              />

              <Card>
                <CardHeader
                  title="Inventory waterfall analysis"
                  subheader="Step-by-step breakdown of inventory asset rollforward"
                />
                <CardContent>
                  <AssetWaterfallChart
                    data={activeInventoryBucket}
                    currencyCode={activeCurrencyCode}
                    metricType={metricType}
                    isLoading={
                      inventoryRollforwardData.isLoading || isInvalidDateRange
                    }
                  />
                </CardContent>
              </Card>
            </Stack>
          )}
        </>
      )}

      {params.tabType === 'fixed-assets' && (
        <>
          {isDateRangeError && (
            <Alert severity="error">
              The date range selected exceeds the maximum allowed range. Please
              select a smaller date range.
            </Alert>
          )}

          {showLoader ? (
            <RollforwardReportLoader
              reportType="fixed-assets"
              isDataReady={!fixedAssetsRollforwardData.isLoading}
              onComplete={handleLoaderComplete}
            />
          ) : fixedAssetsRollforwardData.isError && !isDateRangeError ? (
            <Alert severity="error">
              Unable to load fixed-asset rollforward data. Please try again.
            </Alert>
          ) : !fixedHasData &&
            !isDateRangeError &&
            !isInvalidDateRange &&
            !fixedAssetsRollforwardData.isError ? (
            <Alert severity="info">
              No fixed-asset rollforward data for the selected date range
            </Alert>
          ) : (
            <Stack spacing={3}>
              <AssetRollforwardSummaryCards
                data={activeFixedBucket}
                currencyCode={activeCurrencyCode}
                metricType={metricType}
              />

              <Card>
                <CardHeader
                  title="Fixed asset waterfall analysis"
                  subheader="Step-by-step breakdown of fixed-asset rollforward"
                />
                <CardContent>
                  <AssetWaterfallChart
                    data={activeFixedBucket}
                    currencyCode={activeCurrencyCode}
                    metricType={metricType}
                    isLoading={
                      fixedAssetsRollforwardData.isLoading || isInvalidDateRange
                    }
                  />
                </CardContent>
              </Card>
            </Stack>
          )}
        </>
      )}
      <SupportSnackbar
        isOpen={downloadExport.isError}
        onClose={() => downloadExport.reset()}
        message="An error occurred while exporting the rollforward report."
      />
    </PageContent>
  );
}
